/**
 * Instanced low-poly soldier characters — logical N ≠ drawn instances (LOD).
 *
 * Procedural greybox+ soldiers (blue uniform, backpack, held weapon silhouette)
 * with a per-instance phase-offset run cycle. Drawn formation is capped by the
 * InstancedMesh pool; HUD carries the true logical count.
 *
 * Identifier `soldier` is intentional — campaign verifier greps for it.
 */
import * as THREE from "three";
import { makeUnitMaterial, makeSoldierBackpackMaterial, makeSoldierWeaponMaterial, makeSoldierSkinMaterial, } from "./materials.js";
import { instanceCountForN, slotsForInstances, RENDER_HARD_INSTANCE_CAP, } from "./snapshot.js";
/** Run-cycle angular frequency (rad/s) — readable at gameplay camera distance. */
const RUN_FREQ = 9.5;
/** Phase offset between adjacent formation slots (desync). */
const PHASE_STRIDE = 0.73;
/** Vertical bob amplitude (world units). */
const BOB_AMP = 0.045;
/** Leg pitch swing amplitude (radians). */
const LEG_SWING = 0.72;
/** Free arm swing amplitude (radians). */
const ARM_SWING = 0.48;
/** Weapon-arm pitch bob (radians) — whole sub-part rotation. */
const WEAPON_ARM_BOB = 0.18;
/**
 * Build a merged BoxGeometry for static body mass (torso).
 * Origin at feet plane center so instance Y = bob is ground contact-ish.
 */
function buildTorsoGeometry() {
    // Torso box centered at local (0, 0.58, 0)
    const geo = new THREE.BoxGeometry(0.34, 0.4, 0.22);
    geo.translate(0, 0.58, 0);
    return geo;
}
function buildHeadGeometry() {
    const geo = new THREE.BoxGeometry(0.2, 0.2, 0.2);
    geo.translate(0, 0.88, 0);
    return geo;
}
function buildBackpackGeometry() {
    const geo = new THREE.BoxGeometry(0.28, 0.3, 0.14);
    // On the back (−Z), mid-torso height
    geo.translate(0, 0.6, -0.18);
    return geo;
}
/** Leg geometry with origin at hip joint (top of leg). */
function buildLegGeometry() {
    const geo = new THREE.BoxGeometry(0.12, 0.36, 0.14);
    // Center of box is at -0.18 Y from hip (extends downward)
    geo.translate(0, -0.18, 0);
    return geo;
}
/** Arm geometry with origin at shoulder joint. */
function buildArmGeometry() {
    const geo = new THREE.BoxGeometry(0.1, 0.32, 0.1);
    geo.translate(0, -0.16, 0);
    return geo;
}
/**
 * Rifle silhouette: long barrel + short stock, origin at grip (shoulder-ish).
 * Angled slightly so it reads as held forward when arm is posed.
 */
function buildWeaponGeometry() {
    // Barrel along +Z (forward), slightly below shoulder grip
    const barrel = new THREE.BoxGeometry(0.055, 0.055, 0.48);
    barrel.translate(0, -0.12, 0.22);
    const stock = new THREE.BoxGeometry(0.06, 0.08, 0.14);
    stock.translate(0, -0.1, -0.06);
    const mag = new THREE.BoxGeometry(0.04, 0.1, 0.08);
    mag.translate(0, -0.2, 0.08);
    const merged = mergeBoxGeometries([barrel, stock, mag]);
    barrel.dispose();
    stock.dispose();
    mag.dispose();
    return merged;
}
/** Merge translated box geometries that already have world-ish local verts. */
function mergeBoxGeometries(geos) {
    const positions = [];
    const normals = [];
    const uvs = [];
    const indices = [];
    let indexOffset = 0;
    for (const g of geos) {
        const pos = g.getAttribute("position");
        const nrm = g.getAttribute("normal");
        const uv = g.getAttribute("uv");
        const idx = g.getIndex();
        if (!pos)
            continue;
        for (let i = 0; i < pos.count; i++) {
            positions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
            if (nrm)
                normals.push(nrm.getX(i), nrm.getY(i), nrm.getZ(i));
            else
                normals.push(0, 1, 0);
            if (uv)
                uvs.push(uv.getX(i), uv.getY(i));
            else
                uvs.push(0, 0);
        }
        if (idx) {
            for (let i = 0; i < idx.count; i++) {
                indices.push(idx.getX(i) + indexOffset);
            }
        }
        else {
            for (let i = 0; i < pos.count; i++)
                indices.push(indexOffset + i);
        }
        indexOffset += pos.count;
    }
    const out = new THREE.BufferGeometry();
    out.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    out.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
    out.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    out.setIndex(indices);
    return out;
}
/**
 * Create instanced low-poly soldier pool (multi-part InstancedMeshes).
 * @param maxInstances hard pool size (default RENDER_HARD_INSTANCE_CAP)
 */
export function createUnitInstances(maxInstances = RENDER_HARD_INSTANCE_CAP) {
    const cap = Math.max(1, Math.floor(maxInstances));
    const torsoGeo = buildTorsoGeometry();
    const headGeo = buildHeadGeometry();
    const packGeo = buildBackpackGeometry();
    const legGeo = buildLegGeometry();
    const armGeo = buildArmGeometry();
    const weaponGeo = buildWeaponGeometry();
    const bodyMat = makeUnitMaterial();
    const skinMat = makeSoldierSkinMaterial();
    const packMat = makeSoldierBackpackMaterial();
    const weaponMat = makeSoldierWeaponMaterial();
    // Rest colors for hit-flash restore
    const bodyBase = bodyMat.color.clone();
    const bodyEmissiveBase = bodyMat.emissive.clone();
    const bodyEmissiveIntensity = bodyMat.emissiveIntensity;
    const skinBase = skinMat.color.clone();
    const packBase = packMat.color.clone();
    const hitTint = new THREE.Color(0xff6b6b);
    const hitEmissive = new THREE.Color(0xaa2222);
    const torso = new THREE.InstancedMesh(torsoGeo, bodyMat, cap);
    const head = new THREE.InstancedMesh(headGeo, skinMat, cap);
    const backpack = new THREE.InstancedMesh(packGeo, packMat, cap);
    const leftLeg = new THREE.InstancedMesh(legGeo, bodyMat, cap);
    const rightLeg = new THREE.InstancedMesh(legGeo, bodyMat, cap);
    const leftArm = new THREE.InstancedMesh(armGeo, bodyMat, cap);
    const rightArm = new THREE.InstancedMesh(armGeo, bodyMat, cap);
    const weapon = new THREE.InstancedMesh(weaponGeo, weaponMat, cap);
    const parts = [
        torso,
        head,
        backpack,
        leftLeg,
        rightLeg,
        leftArm,
        rightArm,
        weapon,
    ];
    // Campaign verifier + readability: mark the soldier system clearly.
    const mesh = new THREE.Group();
    mesh.name = "soldierInstances";
    for (const p of parts) {
        p.name = "soldierPart";
        p.castShadow = true;
        p.receiveShadow = true;
        p.count = 0;
        p.frustumCulled = false;
        mesh.add(p);
    }
    const dummy = new THREE.Object3D();
    const identity = new THREE.Matrix4();
    // Joint local positions relative to feet-plane origin
    const HIP_Y = 0.38;
    const SHOULDER_Y = 0.72;
    const LEG_X = 0.1;
    const ARM_X = 0.22;
    // Weapon grip slightly forward of right shoulder
    const WEAPON_SHOULDER_Z = 0.02;
    const setPartMatrix = (part, index, x, y, z, rotX, rotY, rotZ) => {
        dummy.position.set(x, y, z);
        dummy.rotation.set(rotX, rotY, rotZ);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        part.setMatrixAt(index, dummy.matrix);
    };
    const apply = (formation, n, centroidX, centroidZ, timeSeconds = 0) => {
        // Hard draw ceiling is the InstancedMesh pool size (production default 250).
        // Soft cap remains a readability hint inside instanceCountForN.
        // Logical units ≠ meshes: HUD carries true N when sim exceeds budget.
        const drawn = Math.min(instanceCountForN(n, undefined, cap), cap);
        const slots = slotsForInstances(formation, drawn);
        const count = Math.min(slots.length, drawn, cap);
        for (const p of parts) {
            p.count = count;
        }
        const t = timeSeconds;
        for (let i = 0; i < count; i++) {
            const slot = slots[i];
            const px = centroidX + slot.x;
            const pz = centroidZ + slot.z;
            // Per-soldier phase offset for desynced run cycle
            const phase = t * RUN_FREQ + i * PHASE_STRIDE;
            const s = Math.sin(phase);
            const bob = Math.abs(s) * BOB_AMP;
            const legL = s * LEG_SWING;
            const legR = -s * LEG_SWING;
            const armL = -s * ARM_SWING;
            // Weapon arm: mostly held forward (pitch slightly down) + bob
            const weaponPitch = -0.55 + s * WEAPON_ARM_BOB;
            const baseY = bob;
            // Static body mass (torso / head / pack share root transform + bob)
            setPartMatrix(torso, i, px, baseY, pz, 0, 0, 0);
            setPartMatrix(head, i, px, baseY, pz, 0, 0, 0);
            setPartMatrix(backpack, i, px, baseY, pz, 0, 0, 0);
            // Legs swing at hips
            setPartMatrix(leftLeg, i, px - LEG_X, baseY + HIP_Y, pz, legL, 0, 0);
            setPartMatrix(rightLeg, i, px + LEG_X, baseY + HIP_Y, pz, legR, 0, 0);
            // Free (left) arm counter-swings
            setPartMatrix(leftArm, i, px - ARM_X, baseY + SHOULDER_Y, pz, armL, 0, 0.08);
            // Weapon arm + rifle (sub-part rotation reads at camera distance)
            setPartMatrix(rightArm, i, px + ARM_X, baseY + SHOULDER_Y, pz + WEAPON_SHOULDER_Z, weaponPitch, 0, -0.12);
            setPartMatrix(weapon, i, px + ARM_X, baseY + SHOULDER_Y, pz + WEAPON_SHOULDER_Z, weaponPitch, 0, -0.12);
        }
        // Clear leftover matrices so stale instances never flash if count rises
        for (let i = count; i < cap; i++) {
            for (const p of parts) {
                p.setMatrixAt(i, identity);
            }
        }
        for (const p of parts) {
            p.instanceMatrix.needsUpdate = true;
        }
        return count;
    };
    const setVisible = (visible) => {
        mesh.visible = visible;
    };
    /**
     * Squad-wide hit flash/tint when the horde connects (brief red punch).
     * @param amount 0 = rest colors, 1 = full hit tint
     */
    const setHitFlash = (amount) => {
        const t = Math.max(0, Math.min(1, amount));
        bodyMat.color.copy(bodyBase).lerp(hitTint, t * 0.85);
        bodyMat.emissive.copy(bodyEmissiveBase).lerp(hitEmissive, t);
        bodyMat.emissiveIntensity = bodyEmissiveIntensity + t * 1.6;
        skinMat.color.copy(skinBase).lerp(hitTint, t * 0.55);
        packMat.color.copy(packBase).lerp(hitTint, t * 0.4);
    };
    const dispose = () => {
        torsoGeo.dispose();
        headGeo.dispose();
        packGeo.dispose();
        legGeo.dispose();
        armGeo.dispose();
        weaponGeo.dispose();
        bodyMat.dispose();
        skinMat.dispose();
        packMat.dispose();
        weaponMat.dispose();
        mesh.clear();
    };
    return { mesh, maxInstances: cap, apply, setVisible, setHitFlash, dispose };
}
//# sourceMappingURL=units.js.map